import { useEffect, useState, useCallback } from 'react';
import { Sliders, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRecoilValue } from 'recoil';
import { useMarketCreation } from './hooks/useMarketCreation';
import { useWallet } from './hooks/useWallet';
import { useIsMobile } from './hooks/useMobile';
import { useQuestionFlow } from './hooks/useQuestionFlow';
import { useChatManager } from './hooks/useChatManager';
import { useGasEstimation } from './hooks/useGasEstimation';
import { useMarketDeployment } from './hooks/useMarketDeployment';
import { useHeaderContext } from './HeaderContext';
import { BackgroundRain } from './BackgroundRain';
import { FuturisticWelcomeScreen } from './FuturisticWelcomeScreen';
import { ChatBubble, TypingIndicator } from './ChatBubble';
import { ChatSummaryBubble } from './ChatSummaryBubble';
import { DescriptionEditor } from './DescriptionEditor';
import { FuturisticOutcomesEditor } from './FuturisticOutcomesEditor';
import { VisibilitySelector } from './VisibilitySelector';
import { TimelinePicker } from './TimelinePicker';
import { ResolverSelector } from './ResolverSelector';
import { LiquidityWithFees } from './LiquidityWithFees';
import { MarketDetailPreview } from './MarketDetailPreview';
import { SuccessPopupWithConfetti } from './SuccessPopupWithConfetti';
import SolvencyLimitModal from '../SolvencyLimitModal/SolvencyLimitModal';
import { DeployingPopup } from './DeployingPopup';
import { BottomTabBar } from './BottomTabBar';
import { RestorePrompt } from './RestorePrompt';
import { EditPickerBubbles } from './EditPickerBubbles';
import { QuestionInput } from './QuestionInput';
import { VagueQuestionPrompt } from './VagueQuestionPrompt';
import { WalletErrorBubble } from './WalletErrorMessages';
import {
  userSmartAccount,
  userSmartAccountRainBalance,
  userSmartAccountUsdtBalance,
} from '../RecoilAtom';
import { USDT_MOCK_VALUE } from '../../utils/constants';

export const CreateMarketChat = ({ connectWalletModal }) => {
  const isMobile = useIsMobile();
  const { setHeaderAutoHide } = useHeaderContext();
  const smartAccountUsdtBalance = useRecoilValue(userSmartAccountUsdtBalance);
  const smartAccountRainBalance = useRecoilValue(userSmartAccountRainBalance);

  const {
    marketData,
    currentStep,
    isProcessing,
    processQuestion,
    confirmDescription,
    confirmOutcomes,
    setVisibility,
    setResolution,
    setLiquidity,
    finishDeploy,
    reset,
    setCurrentStep,
    updateMarketData,
  } = useMarketCreation();

  const {
    wallet,
    walletError,
    setWalletError,
    connectWallet,
  } = useWallet({ connectWalletModal });

  const questionFlow = useQuestionFlow({ updateMarketData, setCurrentStep });
  const {
    isClassifying,
    vagueStage,
    setVagueStage,
    setPendingVagueQuestion,
    currentClarifyQuestion,
    refinedQuestion,
    setPendingCorrectionType,
    pendingResolutionMethod,
    classifyAndRoute,
    proceedToGenerateAndLiquidity,
    handleAutocompleteQuestion,
    handleClarificationAnswer: qfHandleClarification,
    handleAcceptRefined: qfHandleAcceptRefined,
    reclassifyForRephrase,
    resetQuestionFlow,
    startManualFlow,
    startRephraseFlow,
    confirmManualAndProceed,
  } = questionFlow;

  const effectiveIsProcessing = isProcessing || isClassifying;

  const chatManager = useChatManager({
    currentStep,
    setCurrentStep,
    isMobile,
    walletConnected: wallet.connected,
    reset,
  });
  const {
    chatEndRef,
    mainRef,
    chatHistory,
    setChatHistory,
    showWelcome,
    setShowWelcome,
    stepHistory,
    activeTab,
    setActiveTab,
    chatCollapsed,
    setChatCollapsed,
    savedSnapshot,
    setSavedSnapshot,
    selectedCategories,
    getLiquidityStepMessage,
    handleCategorySelect,
    navigateToStep,
    handleBack,
    handleRestoreKeep,
    handleRestoreReset,
    handleReset: chatReset,
    handleTabChange,
    handleOpenChat,
    handleCloseChat,
  } = chatManager;
  const gasEstimation = useGasEstimation({ marketData });
  const {
    gasFeeInUsdt,
    gasFeesSpinner,
    gasError,
    setLiquidityAmount,
    calculateGas: debouncedCalculateGas,
    isSolvencyLimitReached,
  } = gasEstimation;
  const [liquidityCurrency, setLiquidityCurrency] = useState('usdr');
  const [rephraseFromEdit, setRephraseFromEdit] = useState(false);
  const [showSolvencyModal, setShowSolvencyModal] = useState(false);
  const deployment = useMarketDeployment({
    marketData,
    updateMarketData,
    finishDeploy,
    gasFeeInUsdt,
    onSolvencyError: () => setShowSolvencyModal(true),
  });
  const {
    handleMarketConfirm,
    getOracleFees,
    isButtonDisabled,
    oracleFixedFee,
  } = deployment;
  const smartAccount = useRecoilValue(userSmartAccount);
  useEffect(() => {
    if (currentStep !== 'liquidity') return;
    const amount = gasEstimation.liquidityAmount;
    if (!amount || amount <= 0) return;
    const hasRealOutcomes = marketData.outcomes?.some(
      (o) => o.label !== 'Unclassified' && o.label !== 'None'
    );
    const shouldRetry =
      (gasError === 'smart-account-not-ready' && smartAccount) ||
      (gasError === 'missing-outcomes' && hasRealOutcomes);

    if (shouldRetry) {
      debouncedCalculateGas(amount, liquidityCurrency);
    }
  }, [smartAccount, gasError, currentStep, marketData.outcomes]);
  useEffect(() => {
    setHeaderAutoHide(false);
    return () => setHeaderAutoHide(false);
  }, [showWelcome, setHeaderAutoHide, isMobile]);
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft' && currentStep !== 'question' && currentStep !== 'success') {
        e.preventDefault();
        handleBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, handleBack]);
  const handleQuestionSubmit = useCallback(
    async (question, autocompleteData) => {
      setShowWelcome(false);
      setChatHistory([
        { type: 'user', content: question, targetStep: 'question', summaryData: { summary: question, icon: '❓' } },
      ]);
      if (autocompleteData) {
        handleAutocompleteQuestion(question, autocompleteData);
        setCurrentStep('liquidity');
        setChatHistory((prev) => [
          ...prev,
          getLiquidityStepMessage(),
        ]);
        return;
      }
      const result = await classifyAndRoute(question);
      switch (result.type) {
        case 'restricted':
          setChatHistory((prev) => [
            ...prev,
            {
              type: 'ai',
              content: 'This topic is not supported for prediction markets on this platform. Please submit a different question.',
              component: 'restricted-message',
            },
          ]);
          break;
        case 'spelling':
          setChatHistory((prev) => [
            ...prev,
            {
              type: 'ai',
              content: 'It looks like there might be a small typo in your question. Want to fix it?',
              component: 'vague-suggest',
            },
          ]);
          break;
        case 'semantic':
          setChatHistory((prev) => [
            ...prev,
            {
              type: 'ai',
              content: "This phrasing doesn't fully make sense as a prediction. Here's a clearer version:",
              component: 'vague-suggest',
            },
          ]);
          break;
        case 'needs-clarification':
          setChatHistory((prev) => [
            ...prev,
            {
              type: 'ai',
              content: `This question is a bit general for a prediction market. Let me help you make it more specific! 🎯\n\n${result.clarifyingQuestion || ''}`,
              component: 'vague-details',
            },
          ]);
          break;
        case 'refined-from-vague':
          setChatHistory((prev) => [
            ...prev,
            {
              type: 'ai',
              content: "Based on your question, here's a refined version for your market:",
              component: 'vague-suggest',
            },
          ]);
          break;
        case 'clear': {
          const genResult = await proceedToGenerateAndLiquidity(question, result.resolutionMethod);
          if (genResult.success) {
            setCurrentStep('liquidity');
            setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
          } else {
            try {
              await processQuestion(question);
              setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
            } catch {
              setChatHistory((prev) => [
                ...prev,
                { type: 'ai', content: 'Something went wrong generating your market. Please try again.' },
              ]);
            }
          }
          break;
        }
        default: {
          try {
            await processQuestion(question);
            setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
          } catch {
            setChatHistory((prev) => [
              ...prev,
              { type: 'ai', content: 'Something went wrong processing your question. Please try rephrasing it.' },
            ]);
          }
        }
      }
    },
    [
      setShowWelcome, setChatHistory, setCurrentStep,
      handleAutocompleteQuestion, classifyAndRoute, proceedToGenerateAndLiquidity,
      processQuestion, getLiquidityStepMessage,
    ]
  );
  const handleClarificationAnswer = useCallback(
    async (answer) => {
      setChatHistory((prev) => [...prev, { type: 'user', content: answer }]);
      const result = await qfHandleClarification(answer);
      if (result.type === 'needs-clarification') {
        setChatHistory((prev) => [
          ...prev,
          { type: 'ai', content: result.clarifyingQuestion, component: 'vague-details' },
        ]);
      } else if (result.type === 'refined') {
        setChatHistory((prev) => [
          ...prev,
          { type: 'ai', content: "Based on your answers, here's a refined question for your market:", component: 'vague-suggest' },
        ]);
      } else {
        setChatHistory((prev) => [
          ...prev,
          { type: 'ai', content: "Sorry, I had trouble processing that. Let's continue with your original question." },
        ]);
        setVagueStage('none');
      }
    },
    [qfHandleClarification, setVagueStage, setChatHistory]
  );
  const handleAcceptRefined = useCallback(
    async (editedQuestion) => {
      const result = await qfHandleAcceptRefined(editedQuestion);
      const finalQuestion = result.question || editedQuestion || refinedQuestion;

      if (result.type === 'needs-clarification') {
        setChatHistory([
          { type: 'user', content: finalQuestion, targetStep: 'question', summaryData: { summary: finalQuestion, icon: '❓' } },
        ]);
        setChatHistory((prev) => [
          ...prev,
          {
            type: 'ai',
            content: `Great! Now let me help make it more specific for a prediction market 🎯\n\n${result.clarifyingQuestion || ''}`,
            component: 'vague-details',
          },
        ]);
        return;
      }
      if (result.type === 'refined-from-vague') {
        setChatHistory([
          { type: 'user', content: finalQuestion, targetStep: 'question', summaryData: { summary: finalQuestion, icon: '❓' } },
        ]);
        setChatHistory((prev) => [
          ...prev,
          { type: 'ai', content: "Based on your question, here's a refined version:", component: 'vague-suggest' },
        ]);
        return;
      }
      if (result.type === 'clear' || result.type === 'accepted') {
        setChatHistory([
          { type: 'user', content: finalQuestion, targetStep: 'question', summaryData: { summary: finalQuestion, icon: '❓' } },
        ]);
        const genResult = await proceedToGenerateAndLiquidity(finalQuestion, result.resolutionMethod);
        if (genResult.success) {
          setCurrentStep('liquidity');
        }
        setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
        return;
      }
      setChatHistory([
        { type: 'user', content: finalQuestion, targetStep: 'question', summaryData: { summary: finalQuestion, icon: '❓' } },
      ]);
      await processQuestion(finalQuestion);
      setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
    },
    [qfHandleAcceptRefined, refinedQuestion, proceedToGenerateAndLiquidity, processQuestion, setCurrentStep, setChatHistory, getLiquidityStepMessage]
  );
  const handleVagueClarifyYes = () => {
    startRephraseFlow();
    setPendingVagueQuestion('');
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: 'Yes, let me rephrase' },
      { type: 'ai', content: 'No problem! Go ahead and type your new question:', component: 'rephrase-input' },
    ]);
    setShowWelcome(false);
  };
  const handleRephraseFromRefined = () => {
    setPendingCorrectionType(null);
    startManualFlow();
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: 'Use my original question' },
      { type: 'ai', content: "Got it! Just so you know — this type of question can't be resolved by AI automatically. You'll need to resolve this market manually when the time comes. If that works for you, let's continue!", component: 'vague-manual' },
    ]);
  };
  const handleEditQuestionFromRefined = () => {
    setRephraseFromEdit(true);
    setPendingCorrectionType(null);
    startRephraseFlow();
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: 'I want to edit the question' },
      { type: 'ai', content: 'Edit the question below:', component: 'rephrase-input' },
    ]);
  };
  const handleRephraseSubmit = async (newQuestion) => {
    if (rephraseFromEdit) {
      setRephraseFromEdit(false);
      const result = await reclassifyForRephrase(newQuestion);
      if (result.type === 'clear') {
        setVagueStage('none');
        setChatHistory((prev) => [...prev, { type: 'user', content: newQuestion }]);
        setPendingVagueQuestion('');
        const genResult = await proceedToGenerateAndLiquidity(newQuestion, result.resolutionMethod);
        if (genResult.success) setCurrentStep('liquidity');
        setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
        return;
      }
      setPendingVagueQuestion(newQuestion);
      startManualFlow();
      setChatHistory((prev) => [
        ...prev,
        { type: 'user', content: newQuestion },
        { type: 'ai', content: "Got it! Just so you know — this type of question can't be resolved by AI automatically. You'll need to resolve this market manually when the time comes. If that works for you, let's continue!", component: 'vague-manual' },
      ]);
      return;
    }
    setVagueStage('none');
    setChatHistory([
      { type: 'user', content: newQuestion, targetStep: 'question', summaryData: { summary: newQuestion, icon: '❓' } },
    ]);
    const genResult = await proceedToGenerateAndLiquidity(newQuestion, pendingResolutionMethod);
    if (genResult.success) setCurrentStep('liquidity');
    setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
  };
  const handleVagueClarifyNo = () => {
    startManualFlow();
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: 'No, this is what I meant' },
      { type: 'ai', content: "Got it! Just so you know — this type of question can't be resolved by AI automatically. You'll need to resolve this market manually when the time comes. If that works for you, let's continue!", component: 'vague-manual' },
    ]);
  };
  const handleVagueGoBack = () => {
    if (vagueStage === 'explain-manual' && refinedQuestion) {
      setVagueStage('suggest-refined');
      setChatHistory((prev) => {
        let lastManualIdx = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].component === 'vague-manual') { lastManualIdx = i; break; }
        }
        if (lastManualIdx >= 0) {
          const sliced = prev.slice(0, lastManualIdx - 1);
          return [...sliced, { type: 'ai', content: "Based on your answers, here's a refined question for your market:", component: 'vague-suggest' }];
        }
        return prev;
      });
    } else {
      resetQuestionFlow();
      setChatHistory((prev) => [prev[0]]);
      setCurrentStep('question');
    }
  };

  const handleVagueManualConfirm = async () => {
    const questionToUse = confirmManualAndProceed();
    setChatHistory((prev) => [...prev, { type: 'user', content: 'I understand, continue' }]);
    const genResult = await proceedToGenerateAndLiquidity(questionToUse, 'manual');
    updateMarketData({ resolutionMethod: 'manual' });
    if (genResult.success) setCurrentStep('liquidity');
    setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
  };

  const handleQuestionEdit = async (newQuestion) => {
    const snapshot = savedSnapshot;
    const isUnchanged =
      newQuestion?.trim() === (marketData.question || '').trim() &&
      Array.isArray(marketData.outcomes) &&
      marketData.outcomes.length > 0;

    if (isUnchanged && snapshot) {
      if (snapshot.lastStep === 'preview') {
        setChatHistory(snapshot.chatHistory);
        setCurrentStep('preview');
        setSavedSnapshot(null);
        if (isMobile) setActiveTab('preview');
        else setChatCollapsed(true);
        return;
      }
      setChatHistory(snapshot.chatHistory);
      setCurrentStep(snapshot.lastStep);
      setSavedSnapshot(null);
      return;
    }

    const preservedData = {
      liquidity: marketData.liquidity,
      currency: marketData.currency,
      resolutionMethod: marketData.resolutionMethod,
      isPublic: marketData.isPublic,
      resolutionDate: marketData.resolutionDate,
    };

    const genResult = await proceedToGenerateAndLiquidity(newQuestion, preservedData.resolutionMethod);
    if (!genResult.success) {
      setChatHistory((prev) => [
        ...prev.filter((m) => m.component !== 'question-edit'),
        { type: 'ai', content: 'Something went wrong updating your question. Please try again.', component: 'question-edit-error' },
        { type: 'ai', content: 'Edit your question:', component: 'question-edit' },
      ]);
      return;
    }
    updateMarketData({
      liquidity: preservedData.liquidity,
      currency: preservedData.currency,
      resolutionMethod: preservedData.resolutionMethod,
      isPublic: preservedData.isPublic,
      resolutionDate: preservedData.resolutionDate,
    });

    const questionSummary = {
      type: 'user',
      content: newQuestion,
      targetStep: 'question',
      summaryData: { summary: newQuestion, icon: '❓' },
    };

    if (snapshot && snapshot.lastStep === 'preview') {
      const restored = snapshot.chatHistory.filter((m) => m.component !== 'edit-picker');
      setChatHistory([
        ...restored,
        questionSummary,
        { type: 'ai', content: '✏️ Which part would you like to edit?', component: 'edit-picker' },
      ]);
      setCurrentStep('preview');
      setSavedSnapshot(null);
      if (isMobile) setActiveTab('preview');
      else setChatCollapsed(true);
      return;
    }

    if (snapshot) {
      const withoutEditComponent = chatHistory.filter((m) => m.component !== 'question-edit');
      setChatHistory([
        ...withoutEditComponent,
        questionSummary,
        { type: 'ai', content: 'You updated this step. Want to keep your other answers?', component: 'restore' },
      ]);
      return;
    }
    setChatHistory([questionSummary]);
    setSavedSnapshot(null);
    await handleQuestionSubmit(newQuestion);
  };

  const handleDescriptionConfirm = (description) => {
    if (savedSnapshot) {
      updateMarketData({ description });
      if (savedSnapshot.lastStep === 'preview') {
        const restored = savedSnapshot.chatHistory.filter((m) => m.component !== 'edit-picker');
        const summary = { type: 'user', content: description, targetStep: 'description', summaryData: { summary: description, icon: '📝' } };
        setChatHistory([...restored, summary, { type: 'ai', content: '✏️ Which part would you like to edit?', component: 'edit-picker' }]);
        setCurrentStep('preview');
        setSavedSnapshot(null);
        if (isMobile) setActiveTab('preview');
        else setChatCollapsed(true);
        return;
      }
      setChatHistory((prev) => [
        ...prev,
        { type: 'user', content: description, targetStep: 'description', summaryData: { summary: description, icon: '📝' } },
        { type: 'ai', content: 'You updated this step. Want to keep your other answers?', component: 'restore' },
      ]);
      return;
    }
    confirmDescription(description);
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: description, targetStep: 'description', summaryData: { summary: description, icon: '📝' } },
      getLiquidityStepMessage(),
    ]);
  };

  const handleOutcomesConfirm = (outcomes) => {
    const outcomesSummary = outcomes.map((o) => `${o.label}: ${o.probability}%`).join(' • ');
    updateMarketData({ outcomes });
    if (savedSnapshot && savedSnapshot.lastStep === 'preview') {
      const restored = savedSnapshot.chatHistory.filter((m) => m.component !== 'edit-picker');
      const summary = { type: 'user', content: outcomesSummary, targetStep: 'outcomes', summaryData: { summary: `${outcomes.length} outcomes: ${outcomes.map((o) => o.label).join(', ')}`, fullContent: outcomesSummary, icon: '🎯' } };
      setChatHistory([...restored, summary, { type: 'ai', content: '✏️ Which part would you like to edit?', component: 'edit-picker' }]);
      setCurrentStep('preview');
      setSavedSnapshot(null);
      if (isMobile) setActiveTab('preview');
      else setChatCollapsed(true);
      return;
    }
    if (savedSnapshot) {
      setChatHistory((prev) => [
        ...prev,
        { type: 'user', content: outcomesSummary, targetStep: 'outcomes', summaryData: { summary: `${outcomes.length} outcomes: ${outcomes.map((o) => o.label).join(', ')}`, fullContent: outcomesSummary, icon: '🎯' } },
        { type: 'ai', content: 'You updated this step. Want to keep your other answers?', component: 'restore' },
      ]);
      return;
    }
    confirmOutcomes(outcomes);
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: outcomesSummary, targetStep: 'outcomes', summaryData: { summary: `${outcomes.length} outcomes: ${outcomes.map((o) => o.label).join(', ')}`, fullContent: outcomesSummary, icon: '🎯' } },
    ]);
  };

  const handleVisibilitySelect = (isPublic) => {
    const visibilityText = isPublic ? 'Public market - anyone can see and trade' : 'Private market - invite only';
    updateMarketData({ isPublic });
    if (savedSnapshot && savedSnapshot.lastStep === 'preview') {
      const restored = savedSnapshot.chatHistory.filter((m) => m.component !== 'edit-picker');
      const summary = { type: 'user', content: visibilityText, targetStep: 'visibility', summaryData: { summary: visibilityText, icon: isPublic ? '🌍' : '🔒' } };
      setChatHistory([...restored, summary, { type: 'ai', content: '✏️ Which part would you like to edit?', component: 'edit-picker' }]);
      setCurrentStep('preview');
      setSavedSnapshot(null);
      if (isMobile) setActiveTab('preview');
      else setChatCollapsed(true);
      return;
    }
    if (savedSnapshot) {
      setChatHistory((prev) => [
        ...prev,
        { type: 'user', content: visibilityText, targetStep: 'visibility', summaryData: { summary: visibilityText, icon: isPublic ? '🌍' : '🔒' } },
        { type: 'ai', content: 'You updated this step. Want to keep your other answers?', component: 'restore' },
      ]);
      return;
    }
    setVisibility(isPublic);
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: visibilityText, targetStep: 'visibility', summaryData: { summary: visibilityText, icon: isPublic ? '🌍' : '🔒' } },
    ]);
  };

  const handleResolverSelect = (method) => {
    const resolverText = method === 'ai' ? 'AI Resolution - Automatic' : 'Manual Resolution - You decide';
    updateMarketData({ resolutionMethod: method });
    if (savedSnapshot && savedSnapshot.lastStep === 'preview') {
      const restored = savedSnapshot.chatHistory.filter((m) => m.component !== 'edit-picker');
      const summary = { type: 'user', content: resolverText, targetStep: 'resolver', summaryData: { summary: resolverText, icon: method === 'ai' ? '🤖' : '👤' } };
      setChatHistory([...restored, summary, { type: 'ai', content: '✏️ Which part would you like to edit?', component: 'edit-picker' }]);
      setCurrentStep('preview');
      setSavedSnapshot(null);
      if (isMobile) setActiveTab('preview');
      else setChatCollapsed(true);
      return;
    }
    if (savedSnapshot) {
      setChatHistory((prev) => [
        ...prev,
        { type: 'user', content: resolverText, targetStep: 'resolver', summaryData: { summary: resolverText, icon: method === 'ai' ? '🤖' : '👤' } },
        { type: 'ai', content: 'You updated this step. Want to keep your other answers?', component: 'restore' },
      ]);
      return;
    }
    setCurrentStep('preview');
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: resolverText, targetStep: 'resolver', summaryData: { summary: resolverText, icon: method === 'ai' ? '🤖' : '👤' } },
      { type: 'ai', content: "Perfect! I'm moving you to the preview now — you can review and edit all the details there: outcomes, visibility, timeline, and more. 🚀" },
    ]);
  };

  const handleResolutionSelect = (startDate, endDate) => {
    const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    if (endDay.getTime() < today.getTime()) {
      setChatHistory((prev) => [...prev, { type: 'ai', content: 'End date cannot be in the past. Please pick a future date.' }]);
      return;
    }
    if (startDate) {
      const launchDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      if (launchDay.getTime() < today.getTime()) {
        setChatHistory((prev) => [...prev, { type: 'ai', content: 'Start date cannot be in the past. Please pick a future date.' }]);
        return;
      }
      if (endDay.getTime() < launchDay.getTime()) {
        setChatHistory((prev) => [...prev, { type: 'ai', content: 'End date cannot be earlier than launch date. Please pick a later end date.' }]);
        return;
      }
    }
    const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timelineText = startDate ? `${formatDate(startDate)} → ${formatDate(endDate)}` : `Ends: ${formatDate(endDate)}`;
    updateMarketData({ resolutionDate: endDate, startDate: startDate || null });
    if (savedSnapshot && savedSnapshot.lastStep === 'preview') {
      const summary = { type: 'user', content: timelineText, targetStep: 'resolution', summaryData: { summary: timelineText, icon: '📅' } };
      const restored = savedSnapshot.chatHistory
        .filter((m) => m.component !== 'edit-picker')
        .map((m) => (m.type === 'user' && m.targetStep === 'resolution') ? summary : m);
      setChatHistory([...restored, { type: 'ai', content: '✏️ Which part would you like to edit?', component: 'edit-picker' }]);
      setCurrentStep('preview');
      setSavedSnapshot(null);
      if (isMobile) setActiveTab('preview');
      else setChatCollapsed(true);
      return;
    }
    if (savedSnapshot) {
      setChatHistory((prev) => [
        ...prev,
        { type: 'user', content: timelineText, targetStep: 'resolution', summaryData: { summary: timelineText, icon: '📅' } },
        { type: 'ai', content: 'You updated this step. Want to keep your other answers?', component: 'restore' },
      ]);
      return;
    }
    setResolution(endDate);
    updateMarketData({ startDate: startDate || null });
    setChatHistory((prev) => [
      ...prev,
      { type: 'user', content: timelineText, targetStep: 'resolution', summaryData: { summary: timelineText, icon: '📅' } },
    ]);
  };
  const handleLiquidityConfirm = (amount, currency) => {
    const cur = currency || 'usdr';
    if (amount < 0.01) {
      setWalletError('min-liquidity');
      setChatHistory((prev) => [...prev, { type: 'ai', content: 'wallet-error', component: 'wallet-error' }]);
      return;
    }
    const balance = cur === 'rain' ? smartAccountRainBalance : smartAccountUsdtBalance;
    if (amount > balance) {
      setWalletError('insufficient-balance');
      setChatHistory((prev) => [...prev, { type: 'ai', content: 'wallet-error', component: 'wallet-error' }]);
      return;
    }
    if (USDT_MOCK_VALUE > +smartAccountUsdtBalance) {
      setWalletError('insufficient-balance');
      setChatHistory((prev) => [...prev, { type: 'ai', content: 'wallet-error', component: 'wallet-error' }]);
      return;
    }
    if (gasFeeInUsdt <= 0) {
      if (gasError === 'smart-account-not-ready') {
        setWalletError('not-connected');
      } else {
        setWalletError('gas-fee-error');
      }
      setChatHistory((prev) => [...prev, { type: 'ai', content: 'wallet-error', component: 'wallet-error' }]);
      return;
    }
    if (cur === 'rain' && gasFeeInUsdt > +smartAccountUsdtBalance) {
      setWalletError('insufficient-balance');
      setChatHistory((prev) => [...prev, { type: 'ai', content: 'wallet-error', component: 'wallet-error' }]);
      return;
    }
    if (cur === 'usdr') {
      const totalDeduction = amount + gasFeeInUsdt;
      if (totalDeduction > +smartAccountUsdtBalance) {
        setWalletError('insufficient-balance');
        setChatHistory((prev) => [...prev, { type: 'ai', content: 'wallet-error', component: 'wallet-error' }]);
        return;
      }
    }
    setWalletError(null);
    const currencyLabel = cur === 'usdr' ? 'USD' : 'RAIN';
    setLiquidity(amount);
    updateMarketData({ currency: cur });
    if (savedSnapshot && savedSnapshot.lastStep === 'preview') {
      const restored = savedSnapshot.chatHistory.filter((m) => m.component !== 'edit-picker');
      const summary = {
        type: 'user',
        content: `${amount} ${currencyLabel} liquidity`,
        targetStep: 'liquidity',
        summaryData: { summary: `${amount} ${currencyLabel} liquidity`, icon: '💰' },
      };
      setChatHistory([...restored, summary, { type: 'ai', content: '✏️ Which part would you like to edit?', component: 'edit-picker' }]);
      setCurrentStep('preview');
      setSavedSnapshot(null);
      if (isMobile) setActiveTab('preview');
      else setChatCollapsed(true);
      return;
    }
    setCurrentStep('preview');
    updateMarketData({ resolutionMethod: 'ai' });
    setChatHistory((prev) => [
      ...prev,
      {
        type: 'user',
        content: `${amount} ${currencyLabel} liquidity`,
        targetStep: 'liquidity',
        summaryData: { summary: `${amount} ${currencyLabel} liquidity`, icon: '💰' },
      },
      { type: 'ai', content: "Perfect! I'm moving you to the preview now — you can review and edit all the details there: outcomes, visibility, timeline, and more. 🚀" },
    ]);
  };

  const handleLiquidityAmountChange = async (amount, currency) => {
    setLiquidityAmount(amount);
    setLiquidityCurrency(currency);
    updateMarketData({ currency });
    debouncedCalculateGas(amount, currency);
    await getOracleFees(currency);
  };

  const handleEditField = (field, value) => {
    const fieldMap = {
      question: 'question', description: 'description', liquidity: 'liquidity',
      resolution: 'resolutionDate', outcomes: 'outcomes', visibility: 'isPublic',
      currency: 'currency', resolver: 'resolutionMethod', startDate: 'startDate',
    };
    if (fieldMap[field]) updateMarketData({ [fieldMap[field]]: value });
  };

  const handleCreateRelated = async (question) => {
    reset();
    resetQuestionFlow();
    setShowWelcome(false);
    setChatHistory([{ type: 'user', content: question }]);
    setActiveTab('chat');
    setSavedSnapshot(null);
    const genResult = await proceedToGenerateAndLiquidity(question, 'ai');
    if (genResult.success) setCurrentStep('liquidity');
    setChatHistory((prev) => [...prev, getLiquidityStepMessage()]);
  };

  const handleReset = () => {
    chatReset();
    resetQuestionFlow();
    setRephraseFromEdit(false);
  };

  const previewEnabled = marketData.question.length > 0 && !showWelcome && (currentStep === 'preview' || stepHistory.includes('preview'));

  if (showWelcome) {
    return (
      <FuturisticWelcomeScreen
        selectedCategories={selectedCategories}
        onCategorySelect={handleCategorySelect}
        onQuestionSubmit={handleQuestionSubmit}
        isProcessing={effectiveIsProcessing}
      />
    );
  }

  if (currentStep === 'deploying') {
    return (
      <DeployingPopup
        onComplete={(result) => {
          if (result) {
            updateMarketData({
              txHash: result.txHash,
              poolContractAddress: result.poolContractAddress ?? undefined,
              rainPoolId: result.rainPoolId ?? undefined,
              rainUrl: result.rainUrl ?? undefined,
            });
          }
          finishDeploy();
        }}
        onError={() => {
          setWalletError('deploy-failed');
          setCurrentStep('preview');
        }}
      />
    );
  }

  if (currentStep === 'success') {
    return <SuccessPopupWithConfetti marketData={marketData} onClose={handleReset} onCreateRelated={handleCreateRelated} />;
  }

  const chatPanel = (
    <div className="gm-chat-panel">
      <div className="gm-chat-panel-spacer" />
      <div className="gm-chat-panel-messages">
        <AnimatePresence mode="popLayout">
          {chatHistory.map((message, index) => (
            <motion.div
              key={index}
              className="gm-chat-message-wrap"
              layout
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {message.summaryData ? (
                <ChatSummaryBubble
                  summary={message.summaryData.summary}
                  fullContent={message.summaryData.fullContent}
                  onTap={message.targetStep ? () => navigateToStep(message.targetStep) : undefined}
                />
              ) : (
                <ChatBubble
                  type={message.type}
                  onClick={
                    message.targetStep
                      ? () => navigateToStep(message.targetStep)
                      : undefined
                  }
                >
                  <p className="gm-chat-bubble-text">{message.content}</p>
                </ChatBubble>
              )}

              {message.targetStep === 'question' && currentStep === 'question' && !showWelcome && vagueStage === 'none' && !chatHistory.some((m) => m.component === 'rephrase-input') && !chatHistory.some((m) => m.component === 'question-edit') && (
                <>
                  <div className="gm-chat-backdrop" onClick={() => navigateToStep('cancel-question-edit')} />
                  <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                    <QuestionInput onSubmit={handleQuestionEdit} isProcessing={effectiveIsProcessing} placeholder="Edit your question..." initialValue={marketData.question} />
                  </motion.div>
                </>
              )}
              {message.component === 'question-edit' && currentStep === 'question' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <QuestionInput onSubmit={handleQuestionEdit} isProcessing={effectiveIsProcessing} placeholder="Edit your question..." initialValue={marketData.question} />
                </motion.div>
              )}
              {message.component === 'vague-clarify' && vagueStage === 'ask-clarify' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <VagueQuestionPrompt stage="ask-clarify" onYesMeantSomethingElse={handleVagueClarifyYes} onNoThisIsWhatIMeant={handleVagueClarifyNo} />
                </motion.div>
              )}
              {message.component === 'vague-details' && vagueStage === 'asking-details' && index === chatHistory.length - 1 && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <VagueQuestionPrompt stage="asking-details" clarifyingQuestion={currentClarifyQuestion} onAnswer={handleClarificationAnswer} />
                </motion.div>
              )}
              {message.component === 'vague-suggest' && vagueStage === 'suggest-refined' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <VagueQuestionPrompt stage="suggest-refined" refinedQuestion={refinedQuestion} onAccept={handleAcceptRefined} onRephrase={handleRephraseFromRefined} onEditQuestion={handleEditQuestionFromRefined} />
                </motion.div>
              )}
              {message.component === 'vague-manual' && vagueStage === 'explain-manual' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <VagueQuestionPrompt stage="explain-manual" onUnderstandManual={handleVagueManualConfirm} onGoBack={handleVagueGoBack} />
                </motion.div>
              )}
              {message.component === 'edit-picker' && currentStep === 'preview' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <EditPickerBubbles onSelectStep={navigateToStep} onRestart={handleReset} />
                </motion.div>
              )}
              {message.component === 'rephrase-input' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <QuestionInput onSubmit={handleRephraseSubmit} isProcessing={effectiveIsProcessing} placeholder="Edit your question..." initialValue={refinedQuestion || marketData.question} disableDidYouMean />
                </motion.div>
              )}
              {message.component === 'description' && currentStep === 'description' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <DescriptionEditor suggestedDescription={marketData.description} onConfirm={handleDescriptionConfirm} />
                </motion.div>
              )}
              {message.component === 'outcomes' && currentStep === 'outcomes' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <FuturisticOutcomesEditor outcomes={marketData.outcomes} onConfirm={handleOutcomesConfirm} />
                </motion.div>
              )}
              {message.component === 'visibility' && currentStep === 'visibility' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <VisibilitySelector onSelect={handleVisibilitySelect} />
                </motion.div>
              )}
              {message.component === 'resolution' && currentStep === 'resolution' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <TimelinePicker onSelect={handleResolutionSelect} />
                </motion.div>
              )}
              {message.component === 'resolver' && currentStep === 'resolver' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <ResolverSelector key={`resolver-${index}`} onSelect={handleResolverSelect} />
                </motion.div>
              )}
              {message.component === 'liquidity' && currentStep === 'liquidity' && index === (() => { for (let i = chatHistory.length - 1; i >= 0; i--) { if (chatHistory[i].component === 'liquidity') return i; } return -1; })() && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <LiquidityWithFees
                    onConfirm={handleLiquidityConfirm}
                    initialCurrency={marketData.currency || 'usdr'}
                    maxBalance={
                      wallet.connected
                        ? (liquidityCurrency === 'rain' ? smartAccountRainBalance : smartAccountUsdtBalance)
                        : undefined
                    }
                    gasFeeInUsdt={gasFeeInUsdt}
                    oracleFeeInUsdt={oracleFixedFee}
                    isLoadingFees={gasFeesSpinner}
                    onAmountChange={handleLiquidityAmountChange}
                  />
                </motion.div>
              )}
              {message.component === 'wallet-connect' && !wallet.connected && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <motion.div className="gm-chat-wallet-connect-card">
                    <div className="gm-chat-wallet-connect-row">
                      <motion.div
                        className="gm-chat-wallet-connect-icon"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="gm-text-primary"><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" /></svg>
                      </motion.div>
                      <div>
                        <p className="gm-chat-wallet-connect-title">Connect your wallet</p>
                        <p className="gm-chat-wallet-connect-desc">Required to add liquidity and launch your market</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={async () => {
                        await connectWallet();
                        setChatHistory((prev) => [
                          ...prev.filter((m) => m.component !== 'wallet-connect'),
                          { type: 'ai', content: 'Wallet connected! ✅ Now choose your liquidity:', component: 'liquidity' },
                        ]);
                      }}
                      className="gm-chat-wallet-connect-btn"
                    >
                      Connect Wallet
                    </motion.button>
                  </motion.div>
                </motion.div>
              )}
              {message.component === 'wallet-error' && walletError && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <WalletErrorBubble
                    error={walletError}
                    onConnectWallet={async () => {
                      await connectWallet();
                      setWalletError(null);
                      setChatHistory((prev) => prev.filter((m) => m.component !== 'wallet-error'));
                    }}
                    onSetMinimum={() => {
                      setWalletError(null);
                      setChatHistory((prev) => prev.filter((m) => m.component !== 'wallet-error'));
                    }}
                    onAdjustLiquidity={() => {
                      setWalletError(null);
                      setChatHistory((prev) => prev.filter((m) => m.component !== 'wallet-error'));
                    }}
                    onRetry={() => {
                      setWalletError(null);
                      setChatHistory((prev) => prev.filter((m) => m.component !== 'wallet-error'));
                      if (gasEstimation.liquidityAmount > 0) {
                        debouncedCalculateGas(gasEstimation.liquidityAmount, liquidityCurrency);
                      }
                    }}
                  />
                </motion.div>
              )}
              {message.component === 'restore' && (
                <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="gm-chat-component-wrap">
                  <RestorePrompt onKeep={handleRestoreKeep} onReset={handleRestoreReset} />
                </motion.div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        <AnimatePresence>
          {effectiveIsProcessing && <TypingIndicator />}
        </AnimatePresence>

        <div ref={chatEndRef} />
      </div>
    </div>
  );

  return (
    <>
    <div className="gm-chat-layout">
      {isMobile ? (
        <>
          <main ref={mainRef} data-chat-scroll className="gm-chat-mobile-main">
            {activeTab === 'chat' ? chatPanel : (
              <MarketDetailPreview
                marketData={marketData}
                onConfirm={handleMarketConfirm}
                isProcessing={isProcessing || isButtonDisabled}
                onEdit={handleEditField}
                onNavigateToStep={(step) => { navigateToStep(step); setActiveTab('chat'); }}
                forceManual={false}
                walletError={walletError}
                wallet={wallet}
                onConnectWallet={async () => { await connectWallet(); setWalletError(null); }}
                gasFeeInUsdt={gasFeeInUsdt}
                oracleFeeInUsdt={oracleFixedFee}
                isLoadingFees={gasFeesSpinner}
                onLiquidityAmountChange={handleLiquidityAmountChange}
              />
            )}
          </main>
          <BottomTabBar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            previewEnabled={previewEnabled}
            disabled={isProcessing || isButtonDisabled}
          />
        </>
      ) : (
        <main className="gm-chat-desktop-main">
          <AnimatePresence>
            {!chatCollapsed && (
              <motion.div
                ref={mainRef}
                data-chat-scroll
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '480px', opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="gm-chat-desktop-sidebar"
              >
                {!showWelcome && !chatCollapsed && (
                  <div className="sidebarheader">
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={handleCloseChat}
                    className="gm-chat-desktop-close-btn"
                    title="Close chat"
                  >
                 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M13.5 4.5L4.5 13.5" stroke="#DAD9D9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M4.5 4.5L13.5 13.5" stroke="#DAD9D9" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                  </motion.button>
                  </div>
                )}
                {chatPanel}
              </motion.div>
            )}
          </AnimatePresence>

          {chatCollapsed && (
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={handleOpenChat}
              className="gm-chat-desktop-wizard-btn"
            >
              <Sliders className="gm-icon-sm gm-text-primary" />
              <span className="gm-chat-desktop-wizard-label">Market Wizard</span>
            </motion.button>
          )}

          <div className="gm-chat-desktop-preview-area">
            {previewEnabled ? (
              <div className="gm-chat-desktop-preview-center">
                <MarketDetailPreview
                  marketData={marketData}
                  onConfirm={handleMarketConfirm}
                  isProcessing={isProcessing || isButtonDisabled}
                  onEdit={handleEditField}
                  activeStep={currentStep}
                  onNavigateToStep={navigateToStep}
                  forceManual={false}
                  walletError={walletError}
                  wallet={wallet}
                  onConnectWallet={async () => { await connectWallet(); setWalletError(null); }}
                  gasFeeInUsdt={gasFeeInUsdt}
                  oracleFeeInUsdt={oracleFixedFee}
                  isLoadingFees={gasFeesSpinner}
                  onLiquidityAmountChange={handleLiquidityAmountChange}
                />
              </div>
            ) : (
              <div className="gm-chat-desktop-placeholder">
                <div className="gm-chat-desktop-placeholder-content">
                  <div className="gm-chat-desktop-placeholder-emoji"><img src="\assets\genrate-market-imgs\eye-icon.svg" alt="img" className='img-fluid' /></div>
                  <p className="gm-chat-desktop-placeholder-title">Preview</p>
                  <p className="gm-chat-desktop-placeholder-desc">Start creating a market to see the live preview</p>
                </div>
              </div>
            )}
          </div>
        </main>
      )}
    </div>
    {showSolvencyModal && <SolvencyLimitModal show={showSolvencyModal} onHide={() => setShowSolvencyModal(false)} />}
  </>
  );
};
